import { useEffect, useRef, useState } from "react";
import PosAPI from "../api/inventory/posApi";
import { useDebounce } from "../hooks/useDebounce";
import { inr } from "../utils/posBilling";

export const EMPTY_CUSTOMER = { id: null, name: "", phone: "", email: "", gstin: "", save: true, meta: null };

/**
 * value / onChange = customer state
 *  - Existing customer select -> id set (sale usi Customer se link hogi)
 *  - New customer -> name/phone/email; "save" true ho to invoice ke time Customer collection me ban jaayega
 */
export default function PosCustomerSearch({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const containerRef = useRef(null);
  const nameRef = useRef(null);
  const debounced = useDebounce(query.trim(), 300);

  const selected = !!value.id;
  const formOpen = !selected && (showForm || value.name || value.phone || value.email);

  useEffect(() => {
    if (debounced.length < 2) {
      setResults([]);
      return;
    }
    let active = true;
    setLoading(true);
    PosAPI.customers(debounced)
      .then((res) => {
        if (!active) return;
        setResults(res.code === 200 ? res.data || [] : []);
        setHighlight(0);
        setOpen(true);
      })
      .catch(() => active && setResults([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [debounced]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (c) => {
    onChange({
      ...EMPTY_CUSTOMER,
      id: c.id,
      name: c.name,
      phone: c.phone,
      email: c.email,
      gstin: value.gstin,
      meta: { offlineOrders: c.offlineOrders, totalSpent: c.totalSpent, pendingDue: c.pendingDue },
    });
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const addNew = () => {
    const q = query.trim();
    const digits = q.replace(/\D/g, "");
    const isPhone = digits.length >= 7 && digits.length === q.replace(/[\s+-]/g, "").length;
    onChange({ ...EMPTY_CUSTOMER, phone: isPhone ? digits.slice(-10) : "", name: isPhone ? "" : q, gstin: value.gstin });
    setShowForm(true);
    setQuery("");
    setOpen(false);
    setTimeout(() => nameRef.current?.focus(), 0);
  };

  const reset = () => {
    onChange({ ...EMPTY_CUSTOMER });
    setShowForm(false);
  };

  const options = [...results, { _new: true }];

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = options[highlight];
      o?._new ? addNew() : pick(o);
    } else if (e.key === "Escape") setOpen(false);
  };

  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });

  return (
    <div className="mb-3" ref={containerRef}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Customer</h6>
        {(selected || formOpen) && (
          <button type="button" className="btn btn-link btn-sm p-0" onClick={reset}>
            Walk-in customer
          </button>
        )}
      </div>

      {/* ---------- SELECTED EXISTING CUSTOMER ---------- */}
      {selected && (
        <div className="pos-customer-card">
          <div className="d-flex justify-content-between align-items-start">
            <div className="overflow-hidden">
              <div className="fw-semibold text-truncate">
                <i className="ti ti-user-check text-success me-1" />
                {value.name || "Customer"}
              </div>
              <small className="text-muted d-block">
                {value.phone}
                {value.email && ` · ${value.email}`}
              </small>
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={reset}>
              Change
            </button>
          </div>
          {value.meta && (
            <div className="d-flex flex-wrap gap-1 mt-2">
              <span className="badge bg-light text-dark">{value.meta.offlineOrders} in-store orders</span>
              <span className="badge bg-light text-dark">Spent {inr(value.meta.totalSpent)}</span>
              {value.meta.pendingDue > 0 && <span className="badge bg-danger">Due {inr(value.meta.pendingDue)}</span>}
            </div>
          )}
        </div>
      )}

      {/* ---------- SEARCH ---------- */}
      {!selected && !formOpen && (
        <div className="position-relative">
          <div className="input-group input-group-sm">
            <span className="input-group-text">
              <i className="ti ti-user-search" />
            </span>
            <input
              className="form-control"
              placeholder="Search customer by mobile, name or email"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => debounced.length >= 2 && setOpen(true)}
              onKeyDown={onKeyDown}
              autoComplete="off"
            />
            {loading && (
              <span className="input-group-text">
                <span className="spinner-border spinner-border-sm" />
              </span>
            )}
            <button type="button" className="btn btn-outline-primary" onClick={addNew} title="Add new customer">
              <i className="ti ti-user-plus" />
            </button>
          </div>

          {open && debounced.length >= 2 && (
            <ul className="list-group position-absolute w-100 shadow pos-search-list">
              {results.map((c, idx) => (
                <li
                  key={c.id}
                  className={`list-group-item list-group-item-action ${idx === highlight ? "active" : ""}`}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(c)}
                >
                  <div className="d-flex justify-content-between">
                    <div className="overflow-hidden">
                      <div className="fw-semibold text-truncate">{c.name}</div>
                      <small>
                        {c.phone}
                        {c.email && ` · ${c.email}`}
                      </small>
                    </div>
                    <small className="text-nowrap ms-2">{c.offlineOrders} orders</small>
                  </div>
                </li>
              ))}
              {!loading && results.length === 0 && (
                <li className="list-group-item small text-muted">No customer found.</li>
              )}
              <li
                className={`list-group-item list-group-item-action text-primary ${highlight === options.length - 1 ? "active" : ""}`}
                onMouseEnter={() => setHighlight(options.length - 1)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={addNew}
              >
                <i className="ti ti-user-plus me-1" /> Add new customer "{query.trim()}"
              </li>
            </ul>
          )}
        </div>
      )}

      {/* ---------- NEW CUSTOMER FORM ---------- */}
      {formOpen && (
        <div className="row g-2">
          <div className="col-6">
            <input ref={nameRef} className="form-control form-control-sm" placeholder="Full name" value={value.name} onChange={set("name")} />
          </div>
          <div className="col-6">
            <input
              className="form-control form-control-sm"
              placeholder="Mobile number"
              inputMode="numeric"
              value={value.phone}
              onChange={(e) => onChange({ ...value, phone: e.target.value.replace(/[^0-9+\-\s]/g, "") })}
            />
          </div>
          <div className="col-12">
            <input className="form-control form-control-sm" placeholder="Email (optional)" value={value.email} onChange={set("email")} />
          </div>
          {value.phone && (
            <div className="col-12">
              <div className="form-check small">
                <input
                  id="pos-save-customer"
                  type="checkbox"
                  className="form-check-input"
                  checked={value.save}
                  onChange={(e) => onChange({ ...value, save: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="pos-save-customer">
                  Save to customers (customer can see this order after login)
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {(selected || formOpen) && (
        <input
          className="form-control form-control-sm mt-2"
          placeholder="Customer GSTIN (B2B only)"
          value={value.gstin}
          onChange={(e) => onChange({ ...value, gstin: e.target.value.toUpperCase() })}
        />
      )}
    </div>
  );
}